/**
 * Iter-296 — Client Calendar Integrations.
 *
 * Lets a client connect their Outlook / Microsoft 365 and Google
 * calendars to iEvntür. Imported events flow into a review queue
 * (`ClientCalendarReviewQueue`) — they do NOT auto-create staffing
 * requests. This is intentional per the user's spec: never overwrite
 * manually edited events without warning, always require approval.
 *
 * Page sections:
 *   1. Connect new calendar (Google + Outlook buttons; Outlook is
 *      disabled with a tooltip when the backend reports MS creds are
 *      not configured).
 *   2. Connected accounts table — provider, email, last sync, status,
 *      "Sync now" + "Manage calendars" + "Disconnect" actions.
 *   3. Footer links to the Review Queue and Sync Logs pages.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Loader2, Plus, RefreshCw, Trash2, CalendarDays, ChevronRight,
  CheckCircle2, AlertTriangle, ArrowLeft, ListChecks, ScrollText,
} from 'lucide-react';

const PROVIDER_LABEL = { google: 'Google Calendar', outlook: 'Microsoft Outlook' };

function ProviderBadge({ provider }) {
  return (
    <Badge
      variant="outline"
      className={
        provider === 'google'
          ? 'border-blue-300 text-blue-700 bg-blue-50'
          : 'border-indigo-300 text-indigo-700 bg-indigo-50'
      }
      data-testid={`provider-badge-${provider}`}
    >
      <CalendarDays className="w-3 h-3 mr-1" />
      {PROVIDER_LABEL[provider] || provider}
    </Badge>
  );
}

function StatusBadge({ status, needsReauth }) {
  if (needsReauth) {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-300">
        <AlertTriangle className="w-3 h-3 mr-1" />Reauthorize
      </Badge>
    );
  }
  if (status === 'success') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
        <CheckCircle2 className="w-3 h-3 mr-1" />Synced
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge className="bg-rose-100 text-rose-800 border-rose-300">
        <AlertTriangle className="w-3 h-3 mr-1" />Failed
      </Badge>
    );
  }
  return <Badge variant="secondary">Not synced yet</Badge>;
}

function CalendarPicker({ account, onSave, saving }) {
  const [selected, setSelected] = useState(account.selected_calendar_ids || []);
  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 space-y-2">
      <p className="text-xs font-medium text-slate-700">
        Calendars to import from this account:
      </p>
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {(account.available_calendars || []).map((c) => (
          <label
            key={c.id}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid={`cal-toggle-${c.id}`}
          >
            <Checkbox
              checked={selected.includes(c.id)}
              onCheckedChange={() => toggle(c.id)}
            />
            <span className="font-medium text-slate-800">{c.name}</span>
            {c.primary && (
              <Badge variant="outline" className="text-[10px]">primary</Badge>
            )}
          </label>
        ))}
        {(!account.available_calendars || account.available_calendars.length === 0) && (
          <p className="text-xs text-slate-500 italic">No calendars discovered yet — try Sync now.</p>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => onSave(selected)}
          disabled={saving}
          data-testid="save-cal-selection-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Save selection
        </Button>
      </div>
    </div>
  );
}

export default function ClientCalendarIntegrations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState({ google: { configured: false }, outlook: { configured: false } });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [savingSelection, setSavingSelection] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, acctRes] = await Promise.all([
        api.get('/client/calendar/providers'),
        api.get('/client/calendar/accounts'),
      ]);
      setProviders(provRes.data);
      setAccounts(acctRes.data.accounts || []);
    } catch (e) {
      toast.error('Failed to load calendar integrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // OAuth completion banners
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (connected) {
      toast.success(`${PROVIDER_LABEL[connected] || 'Calendar'} connected. Syncing…`);
      const accountId = searchParams.get('account_id');
      if (accountId) {
        // Auto-trigger first sync
        api.post(`/client/calendar/accounts/${accountId}/sync`).catch(() => {});
      }
      setSearchParams({});
    } else if (error) {
      toast.error('OAuth flow failed — please try again.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const connect = async (provider) => {
    setConnectingProvider(provider);
    try {
      const res = await api.post(`/client/calendar/${provider}/connect`);
      window.location.href = res.data.authorization_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || `Failed to start ${provider} OAuth`;
      toast.error(msg);
      setConnectingProvider(null);
    }
  };

  const sync = async (accountId) => {
    setSyncingId(accountId);
    try {
      const res = await api.post(`/client/calendar/accounts/${accountId}/sync`);
      const { events_new, events_updated, duplicates_found, conflicts_found, status, error } = res.data;
      if (status === 'failed') {
        toast.error(`Sync failed: ${error}`);
      } else {
        toast.success(
          `Sync complete — ${events_new} new, ${events_updated} updated, ${duplicates_found} duplicates, ${conflicts_found} conflicts.`
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncingId(null);
    }
  };

  const disconnect = async (accountId) => {
    if (!window.confirm('Disconnect this calendar? Any imported events stay in your review queue.')) return;
    try {
      await api.delete(`/client/calendar/accounts/${accountId}`);
      toast.success('Calendar disconnected.');
      load();
    } catch (e) {
      toast.error('Failed to disconnect.');
    }
  };

  const saveSelection = async (accountId, calendarIds) => {
    setSavingSelection(true);
    try {
      await api.patch(`/client/calendar/accounts/${accountId}/calendars`, {
        selected_calendar_ids: calendarIds,
      });
      toast.success('Calendar selection saved.');
      load();
    } catch (e) {
      toast.error('Failed to save selection.');
    } finally {
      setSavingSelection(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-calendar-integrations-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-slate-900 truncate">Calendar Integrations</h1>
            <p className="text-xs text-slate-500 truncate">
              Connect Outlook or Google to import events into your review queue.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/client/calendar/review')}
            data-testid="open-queue-btn"
          >
            <ListChecks className="w-4 h-4 mr-1" />
            Review Queue
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client/calendar/sync-logs')}
            data-testid="open-logs-btn"
          >
            <ScrollText className="w-4 h-4 mr-1" />
            Logs
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-5">
        {/* Connect cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card data-testid="connect-google-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ProviderBadge provider="google" />
                Connect Google Calendar
              </CardTitle>
              <CardDescription>Read-only. We never write back to your calendar.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => connect('google')}
                disabled={!providers.google?.configured || connectingProvider === 'google'}
                className="w-full"
                data-testid="connect-google-btn"
              >
                {connectingProvider === 'google' ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-1" />
                )}
                Connect Google
              </Button>
              {!providers.google?.configured && (
                <p className="text-xs text-amber-700 mt-2">
                  Google credentials are missing from the backend. Admin must set
                  GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="connect-outlook-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ProviderBadge provider="outlook" />
                Connect Outlook / 365
              </CardTitle>
              <CardDescription>Read-only via Microsoft Graph. Personal + work accounts.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => connect('outlook')}
                disabled={!providers.outlook?.configured || connectingProvider === 'outlook'}
                className="w-full"
                data-testid="connect-outlook-btn"
              >
                {connectingProvider === 'outlook' ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-1" />
                )}
                Connect Outlook
              </Button>
              {!providers.outlook?.configured && (
                <p className="text-xs text-amber-700 mt-2">
                  Outlook OAuth is not yet configured. Admin needs to add MS_CLIENT_ID
                  and MS_CLIENT_SECRET to backend env.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Connected accounts */}
        <Card data-testid="connected-accounts-card">
          <CardHeader>
            <CardTitle className="text-base">Connected accounts</CardTitle>
            <CardDescription>
              Sync runs pull events into your review queue — they never auto-staff anything.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            )}
            {!loading && accounts.length === 0 && (
              <div
                className="text-center py-10 text-sm text-slate-500"
                data-testid="empty-state-no-accounts"
              >
                <CalendarDays className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p>No calendars connected yet.</p>
                <p className="text-xs mt-1">Connect Google or Outlook above to get started.</p>
              </div>
            )}
            {!loading && accounts.map((acct) => (
              <div
                key={acct.id}
                className="border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`account-row-${acct.id}`}
              >
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <ProviderBadge provider={acct.provider} />
                      <span className="font-medium text-sm text-slate-900 truncate">
                        {acct.display_name || acct.email}
                      </span>
                      <StatusBadge status={acct.last_sync_status} needsReauth={acct.needs_reauth} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {acct.email}
                      {acct.last_sync_at ? ` · last sync ${new Date(acct.last_sync_at).toLocaleString()}` : ''}
                    </p>
                    {acct.last_sync_error && (
                      <p className="text-xs text-rose-600 mt-1 truncate">{acct.last_sync_error}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sync(acct.id)}
                    disabled={syncingId === acct.id}
                    data-testid={`sync-now-btn-${acct.id}`}
                  >
                    {syncingId === acct.id ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    )}
                    Sync
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setExpandedAccountId((cur) => (cur === acct.id ? null : acct.id))
                    }
                    data-testid={`manage-cals-btn-${acct.id}`}
                  >
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${
                        expandedAccountId === acct.id ? 'rotate-90' : ''
                      }`}
                    />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => disconnect(acct.id)}
                    className="text-rose-600 hover:bg-rose-50"
                    data-testid={`disconnect-btn-${acct.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {expandedAccountId === acct.id && (
                  <CalendarPicker
                    account={acct}
                    onSave={(ids) => saveSelection(acct.id, ids)}
                    saving={savingSelection}
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
